import { useEffect, useMemo, useState } from "react";
import { useNavigate, Outlet, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import LoadingIndicator from "../../../../components/common/LoadingIndicator";
import AppTextField from "../../../../components/common/forms/AppTextField";
import AppSelect from "../../../../components/common/forms/AppSelect";
import PageContainer from "../../../../components/common/PageContainer";
import { ICreateLocation } from "./utils/Location-utils";
import { SelectOptionType } from "../../../../components/common/forms/form-types";
import { LocationSchema, LocationInitialValues } from "./utils/schema";
import {
  useGetstreetLocationById,
  useUpdateLocations,
  useCreateLocations,
} from "../../../../hooks/useLocations";
import { appColors, appStyles } from "../../../../theme";
import { usePermission } from "../../../../hooks/usePermissions";
import { Modules, Permissions } from "../../../Users/Roles/roles-utils";
import { Navigate } from "react-router-dom";

//Quins
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import OutlinedInput from "@mui/material/OutlinedInput";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import ListSubheader from "@mui/material/ListSubheader";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import { useGetUserQuery } from "../../../../hooks/useUser";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 320,
      width: 420,
    },
  },
  autoFocus: false,
  MenuListProps: {
    autoFocusItem: false,
  },
};

const LocationForm = () => {
  const hasAddPermission = usePermission(
    Modules.mmdLoc.accessKey,
    Permissions.add,
  );
  const hasEditPermission = usePermission(
    Modules.mmdLoc.accessKey,
    Permissions.edit,
  );
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentLocation = useGetstreetLocationById();
  const updateLocation = useUpdateLocations();
  const createLocation = useCreateLocations();
  const statusOptions: SelectOptionType[] = [
    { id: "true", label: "Active", value: "true" },
    { id: "false", label: "Inactive", value: "false" },
  ];
  const typeOptions: SelectOptionType[] = [
    { id: "Project", label: "Project", value: "Project" },
    { id: "Motorpool", label: "Motorpool", value: "Motorpool" },
    { id: "HO", label: "HO", value: "HO" },
    { id: "Unclassified", label: "Unclassified", value: "Unclassified" },
  ];

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors, isValid, isDirty },
  } = useForm<ICreateLocation>({
    defaultValues: currentLocation.data
      ? {
          id: currentLocation.data.id || "",
          name: currentLocation.data.name || "",
          streetLocation: currentLocation.data.streetLocation || "",
          type: currentLocation.data.type || "",
          isActive: currentLocation.data.isActive ? "true" : "false",
          userIds: currentLocation.data.userIds || [],
        }
      : {
          ...LocationInitialValues,
          isActive: "true",
        },
    mode: "onChange",
    resolver: yupResolver(LocationSchema),
  });

  //Quins
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [initialUsers, setInitialUsers] = useState<string[]>([]);
  const usersQuery = useGetUserQuery();
  const isEditMode = Boolean(currentLocation.data);

  useEffect(() => {
    if (currentLocation.data) {
      const loc = currentLocation.data;
      const userIds = loc.userIds || [];
      setSelectedUsers(userIds);
      setInitialUsers(userIds);
      reset({
        id: loc.id || "",
        name: loc.name || "",
        streetLocation: loc.streetLocation || "",
        type: loc.type || "",
        isActive: loc.isActive ? "true" : "false",
        userIds: userIds,
      });
    } else {
      setSelectedUsers([]);
      setInitialUsers([]);
      reset({
        ...LocationInitialValues,
        isActive: "true",
      });
    }
  }, [currentLocation.data, reset]);

  const isLocationsDirty = useMemo(() => {
    if (selectedUsers.length !== initialUsers.length) return true;
    const sortedSelected = [...selectedUsers].sort();
    const sortedInitial = [...initialUsers].sort();
    return sortedSelected.some(
      (userId, index) => userId !== sortedInitial[index],
    );
  }, [selectedUsers, initialUsers]);

  const isFormDirty = isDirty || isLocationsDirty;

  const candidateUsers = useMemo(() => {
    return usersQuery.data || [];
  }, [usersQuery.data]);

  const [searchUser, setSearchUser] = useState("");

  const filteredUsers = useMemo(() => {
    const q = searchUser.trim().toLowerCase();
    if (!q) return candidateUsers;
    return candidateUsers.filter((user) => {
      const fullName =
        `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();
      const role = (user.roleName || "").toLowerCase();
      const status = user.isActive ? "active" : "inactive";
      return fullName.includes(q) || role.includes(q) || status.includes(q);
    });
  }, [candidateUsers, searchUser]);

  const filteredUserIds = useMemo(
    () => filteredUsers.map((u) => u.id),
    [filteredUsers],
  );

  const isAllSelected =
    filteredUserIds.length > 0 &&
    filteredUserIds.every((id) => selectedUsers.includes(id));

  const isIndeterminate =
    !isAllSelected && filteredUserIds.some((id) => selectedUsers.includes(id));

  const handleUsersChange = (newSelected: string[]) => {
    setSelectedUsers(newSelected);
    setValue("userIds", newSelected, { shouldValidate: true });
  };

  const handleSelectChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const selectedArray = typeof value === "string" ? value.split(",") : value;

    if (selectedArray.includes("all")) {
      if (isAllSelected) {
        const updated = selectedUsers.filter(
          (id) => !filteredUserIds.includes(id),
        );
        handleUsersChange(updated);
      } else {
        const set = new Set([...selectedUsers, ...filteredUserIds]);
        handleUsersChange(Array.from(set));
      }
      return;
    }

    handleUsersChange(selectedArray);
  };

  const handleRemoveUser = (userId: string) => {
    const updated = selectedUsers.filter((id) => id !== userId);
    handleUsersChange(updated);
  };

  if (!hasAddPermission && !hasEditPermission) return <Navigate to="/home" />;

  const onError = (err: typeof errors) => {
    console.error(err);
  };

  const onSubmit = (data: ICreateLocation) => {
    const payload = { ...data, userIds: selectedUsers };
    if (currentLocation.data) {
      updateLocation.mutate(payload);
    } else {
      createLocation.mutate(payload);
    }
  };

  const backTo = () => navigate("/location");

  const isSubmitting = createLocation.isLoading || updateLocation.isLoading;

  if (id !== undefined && currentLocation.isLoading && !currentLocation.data) {
    return <LoadingIndicator show />;
  }
  if (usersQuery.isLoading && !usersQuery.data) {
    return <LoadingIndicator show />;
  }

  return (
    <>
      <PageContainer
        title="Manage My Databases"
        subTitle="Location Master"
        controls={
          <Stack direction="row" gap={2}>
            <Button
              sx={{ backgroundColor: appColors.lightGray, color: "black" }}
              onClick={backTo}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(onSubmit, onError)}
              disabled={
                !isValid ||
                !isFormDirty ||
                isSubmitting ||
                (isEditMode ? !hasEditPermission : !hasAddPermission)
              }
              disableElevation
              variant="contained"
              sx={{
                textTransform: "capitalize",
                borderRadius: 1.5,
                minWidth: 80,
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Save"
              )}
            </Button>
          </Stack>
        }
      >
        <Divider color="black" />
        <Stack
          direction="column"
          gap={6}
          component="form"
          height="100%"
          bgcolor="white"
          py={3}
          boxShadow={appStyles.shadow}
          borderRadius={1}
        >
          <Stack
            direction="row"
            gap={6}
            px={3}
            height="100%"
            overflow="auto"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Stack direction="column" gap={3} width={550}>
              <AppTextField
                required
                label="Name"
                placeholder="Name"
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register("name")}
              />
              <AppTextField
                required
                label="Street Location"
                placeholder="Street Location"
                error={!!errors.streetLocation}
                helperText={errors.streetLocation?.message}
                {...register("streetLocation")}
              />
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <AppSelect
                    label={
                      <>
                        Type{" "}
                        <Typography component={"span"} color="error.main">
                          *
                        </Typography>
                      </>
                    }
                    placeholder="Select here"
                    error={!!errors.type}
                    helperText={errors.type?.message}
                    options={typeOptions}
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name="isActive"
                render={({ field }) => (
                  <AppSelect
                    label="Status"
                    disabled={currentLocation.data ? false : true}
                    placeholder="Select here"
                    error={!!errors.isActive}
                    helperText={errors.isActive?.message}
                    options={statusOptions}
                    {...field}
                  />
                )}
              />
            </Stack>
            <Stack direction="column" gap={1.5} width={400}>
              <Box
                component="label"
                htmlFor="project-members-select"
                sx={{
                  position: "relative",
                  left: -4,
                  fontSize: "15px",
                  fontWeight: 600,
                  color: appColors.formText,
                }}
              >
                Project Members
              </Box>
              <FormControl fullWidth size="small">
                <Select
                  id="project-members-select"
                  multiple
                  displayEmpty
                  value={selectedUsers}
                  onChange={handleSelectChange}
                  onClose={() => setSearchUser("")}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    const selectedIds = selected as string[];
                    if (selectedIds.length === 0) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          Select members for Location
                        </Typography>
                      );
                    }
                    return selectedIds
                      .map((uid) => {
                        const u = usersQuery.data?.find(
                          (user) => user.id === uid,
                        );
                        return u ? `${u.firstName} ${u.lastName}` : "Member";
                      })
                      .join(", ");
                  }}
                  MenuProps={MenuProps}
                  sx={{
                    borderRadius: 1,
                    backgroundColor: "#fff",
                    height: 40,
                  }}
                >
                  <ListSubheader
                    disableSticky={false}
                    sx={{
                      position: "sticky",
                      top: 0,
                      bgcolor: "#fff",
                      zIndex: 3,
                      p: "6px 8px",
                      lineHeight: "normal",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Escape") {
                        e.stopPropagation();
                      }
                    }}
                  >
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Search member..."
                      fullWidth
                      value={searchUser}
                      onChange={(e) => setSearchUser(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon
                              fontSize="small"
                              sx={{ color: "text.secondary" }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: searchUser ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              edge="end"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchUser("");
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                        sx: { fontSize: "13.5px" },
                      }}
                    />
                  </ListSubheader>
                  {filteredUsers.length === 0 && (
                    <MenuItem
                      disabled
                      sx={{ justifyContent: "center", py: 1.5 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No members found
                      </Typography>
                    </MenuItem>
                  )}
                  {filteredUsers.length > 0 && (
                    <MenuItem
                      value="all"
                      sx={{
                        position: "sticky",
                        top: 45,
                        bgcolor: "#fff",
                        zIndex: 2,
                        borderBottom: "1px solid #e0e0e0",
                        display: "flex",
                        alignItems: "center",
                        py: 0.5,
                        gap: 0.5,
                        "&:hover": { bgcolor: "#f5f5f5" },
                      }}
                    >
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                        sx={{ p: 0.5 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {isAllSelected ? "Unselect All" : "Select All"}
                      </Typography>
                    </MenuItem>
                  )}
                  {filteredUsers.map((user) => {
                    const isSelected = selectedUsers.includes(user.id);
                    return (
                      <MenuItem
                        key={user.id}
                        value={user.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          py: 0.5,
                          gap: 0.5,
                        }}
                      >
                        <Checkbox checked={isSelected} sx={{ p: 0.5 }} />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            minWidth: 0,
                            flex: 1,
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontWeight: isSelected ? 600 : 400,
                              flexShrink: 1,
                            }}
                          >
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography
                            sx={{
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                              color: "text.secondary",
                              fontSize: "12.5px",
                            }}
                          >
                            ({user.roleName || "No Role"}){" "}
                            <Box
                              component="span"
                              sx={{
                                color: user.isActive
                                  ? "success.main"
                                  : "error.main",
                                fontWeight: 500,
                              }}
                            >
                              {user.isActive ? "" : " - Inactive"}
                            </Box>
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Box
                sx={{
                  width: 400,
                  height: 335,
                  overflowY: "auto",
                  border: "1px solid #dcdcdc",
                  borderRadius: 1,
                  p: 1.5,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignContent: "flex-start",
                  bgcolor: "#fafafa",
                }}
              >
                {selectedUsers.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ m: "auto" }}
                  >
                    No members assigned to this project
                  </Typography>
                ) : (
                  selectedUsers.map((uid) => {
                    const user = usersQuery.data?.find((u) => u.id === uid);
                    const isActive = user ? user.isActive : true;
                    const displayName = user ? (
                      <span>
                        {user.firstName} {user.lastName}
                        {!isActive && (
                          <span style={{ color: "#d32f2f", fontWeight: 600 }}>
                            {" "}
                            (Inactive)
                          </span>
                        )}
                      </span>
                    ) : (
                      "Former Member"
                    );
                    return (
                      <Chip
                        key={uid}
                        label={displayName}
                        color={isActive ? "primary" : "default"}
                        variant={isActive ? "outlined" : "filled"}
                        onDelete={() => handleRemoveUser(uid)}
                        sx={
                          !isActive
                            ? {
                                backgroundColor: "#e0e0e0",
                                color: "#374151",
                                fontWeight: 500,
                                border: "1px solid #d1d5db",
                                "& .MuiChip-deleteIcon": {
                                  color: "#4b5563",
                                  "&:hover": { color: "#111827" },
                                },
                              }
                            : undefined
                        }
                      />
                    );
                  })
                )}
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </PageContainer>
      <Outlet />
    </>
  );
};

export default LocationForm;
